import {
  IFeeSchedule,
  IPricingRule,
  IScopedServiceCode,
  ITreatmentConfiguration,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  WithRef,
} from '@principle-theorem/shared';
import { compact, first } from 'lodash';
import { PricingRule } from '../pricing-rules/pricing-rule';
import { FeeSchedule } from './fee-schedule';

export class FeeScheduleManager {
  private constructor(
    private _currentSchedule: WithRef<IFeeSchedule>,
    private _inheritedSchedules: WithRef<IFeeSchedule>[]
  ) {}

  /**
   * Async factory — resolves a fee schedule and its full inheritance
   * chain from Firestore.
   */
  static async create(
    scheduleRef: DocumentReference<IFeeSchedule>
  ): Promise<FeeScheduleManager> {
    const schedule = await Firestore.getDoc(scheduleRef);
    const inherited =
      await FeeScheduleManager._resolveInheritanceChain(schedule);
    return new FeeScheduleManager(schedule, inherited);
  }

  /**
   * Async factory — use when you already have the schedule loaded
   * but still need to resolve its inheritance chain from Firestore.
   */
  static async createFromSchedule(
    schedule: WithRef<IFeeSchedule>
  ): Promise<FeeScheduleManager> {
    const inherited =
      await FeeScheduleManager._resolveInheritanceChain(schedule);
    return new FeeScheduleManager(schedule, inherited);
  }

  /**
   * Sync factory — use when you already have the resolved schedule
   * and its inheritance chain (primarily for testing).
   */
  static fromSchedule(
    schedule: WithRef<IFeeSchedule>,
    inherited: WithRef<IFeeSchedule>[]
  ): FeeScheduleManager {
    return new FeeScheduleManager(schedule, inherited);
  }

  private static async _resolveInheritanceChain(
    schedule: WithRef<IFeeSchedule>
  ): Promise<WithRef<IFeeSchedule>[]> {
    const results: WithRef<IFeeSchedule>[] = [];
    const visited = new Set<string>([schedule.ref.path]);
    let current: WithRef<IFeeSchedule> | undefined = schedule;

    while (current?.inheritedFrom) {
      if (visited.has(current.inheritedFrom.path)) {
        break;
      }
      visited.add(current.inheritedFrom.path);
      const inherited: WithRef<IFeeSchedule> | undefined =
        await Firestore.safeGetDoc(current.inheritedFrom);
      if (!inherited) {
        break;
      }
      results.push(inherited);
      current = inherited;
    }

    return results;
  }

  get currentSchedule(): WithRef<IFeeSchedule> {
    return this._currentSchedule;
  }

  getFeeByCode(code: IScopedServiceCode): IPricingRule {
    const fee = FeeSchedule.getServiceFeeByCode(this._currentSchedule, code);
    if (fee !== undefined) {
      return fee;
    }
    return this.getDefaultServiceFee(code);
  }

  getFeeByTreatment(
    treatment: DocumentReference<ITreatmentConfiguration>
  ): number {
    const fee = FeeSchedule.getTreatmentFee(this._currentSchedule, treatment)
      ?.price;
    if (fee !== undefined) {
      return fee;
    }
    return this.getDefaultTreatmentFee(treatment);
  }

  getDefaultServiceFee(code: IScopedServiceCode): IPricingRule {
    const pricingRules = compact(
      this._inheritedSchedules.map((inheritedSchedule) =>
        FeeSchedule.getServiceFeeByCode(inheritedSchedule, code)
      )
    );
    return first(pricingRules) || PricingRule.init();
  }

  getDefaultTreatmentFee(
    treatment: DocumentReference<ITreatmentConfiguration>
  ): number {
    const fees = compact(
      this._inheritedSchedules.map((inheritedSchedule) =>
        FeeSchedule.getTreatmentFeePrice(inheritedSchedule, treatment)
      )
    );
    return first(fees) || 0;
  }

  getDefaultTreatmentPricingRule(
    treatment: DocumentReference<ITreatmentConfiguration>
  ): IPricingRule | undefined {
    const pricingRules = compact(
      this._inheritedSchedules.map(
        (inheritedSchedule) =>
          FeeSchedule.getTreatmentFee(inheritedSchedule, treatment)?.pricingRule
      )
    );
    return first(pricingRules);
  }
}
